<template>
  <info-panel
    v-if="stakeholder"
    :item="stakeholder"
    :title="stakeholder?.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{ $t("labels.showEmptySections") }}</template>
    
    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template #description v-if="settingsValues.showEmptySections || !isEmpty(stakeholder.description)">
      <formatted-read-more-text
        :value="stakeholder.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <!-- TARGETS -->
    <template #label-targets>
      {{ $t("objectNames.target", 2) }}
    </template>
    <template #targets v-if="settingsValues.showEmptySections || targets.length > 0">
      <button-list
        v-if="targets.length > 0"
        :items="targets"
        prop="title"
        @select="openOverlay"
      >
        <template #default="{ item: target }">
          <span v-if="target.title">{{ target.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
      </button-list>
      <p v-else class="message mv-0">
        {{ $t("errors.noX", [$t("objectNames.target", 2)]) }}
      </p>
    </template>

    <!-- ACTIONS -->
    <template #label-actions>
      {{ $t("objectNames.action", 2) }}
    </template>
    <template #actions v-if="settingsValues.showEmptySections || actions.length > 0">
      <button-list
        v-if="actions.length > 0"
        :items="actions"
        prop="title"
        @select="openOverlay"
      >
        <template #default="{ item: action }">
          <span v-if="action.title">{{ action.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
      </button-list>
      <p v-else class="message mv-0">
        {{ $t("errors.noX", [$t("objectNames.action", 2)]) }}
      </p>
    </template>

    <template #label-resources>
      {{ $t("objectNames.resource", 2) }}
    </template>
    <template #resources v-if="settingsValues.showEmptySections || resources.length > 0">
      <button-list :items="resources" prop="title" @select="openOverlay">
        <template #default="{ item: resource }">
          <span v-if="resource.title">{{ resource.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
        <template #placeholder>
          <p class="message mv-0">
            {{ $t("errors.noX", [$t("objectNames.resource", 2)]) }}
          </p>
        </template>
      </button-list>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { isEmpty } from "@/helpers/utility/Text";
import { mapValues } from "@/helpers/utility/CollectionFunctions";

import ButtonList from "@core/ui/ButtonList.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";

import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { StakeholderItem } from "./StakeholderItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";

const props = defineProps<{
  stakeholder: StakeholderItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const infoPanelStore = useInfoPanelStore();

const sections = reactive({
  description: true,
  targets: true,
  resources: true,
  actions: true
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true }
});

const targets = computed(() => {
  if (props.stakeholder == null) return [];
  return useTargetStore().getArrayByIds(props.stakeholder.targetIds);
});
const actions = computed(() => {
  if (props.stakeholder == null) return [];
  return useActionStore().getArrayByIds(props.stakeholder.actionIds);
});
const resources = computed(() => {
  if (props.stakeholder == null) return [];
  return useResourceStore().getArrayByIds(props.stakeholder.resourceIds);
});

// // IMAGE 
// const image = computed(() => {
//   if (props.stakeholder && props.stakeholder.imageId != null) {
//     return imageStore.items[props.stakeholder.imageId];
//   }
//   return null;
// });
// const imageData = computed(() => {
//   if (!image.value) return undefined;
//   return imageStore.mediaData[image.value.id] ?? undefined;
// });

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

// SECTIONS

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.stakeholder = sections;
}

function loadSections() {
  // TODO: add sections depending if feature is disabled / enabled
  const savedSections = infoPanelStore.sections?.stakeholder || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as (keyof typeof sections)] = savedSections[key];
    }
  }
}

loadSections();


function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.stakeholder = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.stakeholder || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as (keyof typeof extraSettings)] = { ...(savedExtraSettings[key] as any) };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style></style>
